// API Endpoints constants
// All backend API endpoint paths are centralized here

// Base API version
export const API_V1 = '/api/v1'

// Auth endpoints
export const AUTH_ENDPOINTS = {
  LOGIN: `${API_V1}/auth/login`,
  REGISTER: `${API_V1}/auth/register`,
  LOGOUT: `${API_V1}/auth/logout`,
  REFRESH: `${API_V1}/auth/refresh`,
  ME: `${API_V1}/auth/me`, // Session validation + user info with RBAC
  CHANGE_PASSWORD: `${API_V1}/auth/change-password`,
}

export const TWO_FACTOR_ENDPOINTS = {
  SETUP: `${API_V1}/two-factor/setup`,
  ENABLE: `${API_V1}/two-factor/enable`,
  VERIFY: `${API_V1}/two-factor/verify`,
  STATUS: `${API_V1}/two-factor/status`,
  REGENERATE_BACKUP_CODES: `${API_V1}/two-factor/backup-codes/regenerate`,
} as const

export const ADMIN_ENDPOINTS = {
  USER_PASSWORD: (id: string) => `${API_V1}/auth/admin/users/${id}/password`,
} as const

// User endpoints
export const USER_ENDPOINTS = {
  LIST: `${API_V1}/users/`,
  PROFILE: `${API_V1}/users/profile`,
  ME: `${API_V1}/users/me`,
  USER_BY_ID: (id: string) => `${API_V1}/users/${id}`,
} as const

// RBAC endpoints
export const RBAC_ENDPOINTS = {
  // Role management
  ROLES: `${API_V1}/rbac/roles`,
  ROLE_BY_ID: (id: string) => `${API_V1}/rbac/roles/${id}`,
  ROLE_PERMISSIONS: (id: string) => `${API_V1}/rbac/roles/${id}/permissions`,
  REVOKE_ROLE_PERMISSION: (roleId: string, permissionId: string) =>
    `${API_V1}/rbac/roles/${roleId}/permissions/${permissionId}`,
  ROLE_USERS: (roleId: string) => `${API_V1}/rbac/roles/${roleId}/users`,

  // User role assignment
  USER_ROLES: (userId: string) => `${API_V1}/rbac/users/${userId}/roles`,
  REVOKE_USER_ROLE: (userId: string, roleId: string) =>
    `${API_V1}/rbac/users/${userId}/roles/${roleId}`,

  // Permission catalog
  PERMISSIONS: `${API_V1}/rbac/permissions`,
  PERMISSION_CATEGORIES: `${API_V1}/rbac/permissions/categories`,
} as const

// Scan endpoints
export const SCAN_ENDPOINTS = {
  NEW: `${API_V1}/scan/new`,
  LIST: `${API_V1}/scan/`,
  SCAN_BY_ID: (scanId: string) => `${API_V1}/scan/${scanId}`,
  DELETE: (scanId: string) => `${API_V1}/scan/${scanId}`,
  GENERATE_ANALYSIS: (scanId: string) => `${API_V1}/scan/${scanId}/generate-analysis`,
  GENERATE_BLOCK: (scanId: string) => `${API_V1}/scan/${scanId}/generate-block`,
  UPDATE_BLOCK: (scanId: string) => `${API_V1}/scan/${scanId}/update-block`,
  CONFIRM: (scanId: string) => `${API_V1}/scan/${scanId}/confirm`,
  AVAILABLE: `${API_V1}/scan/available`,
} as const

// BOB analyse endpoints
export const BOB_ENDPOINTS = {
  NEW: `${API_V1}/bob/new`,
  LIST: `${API_V1}/bob/list`,
  BOB_BY_ID: (bobId: string) => `${API_V1}/bob/${bobId}`,
  DELETE: (bobId: string) => `${API_V1}/bob/${bobId}`,
  UPDATE: (bobId: string) => `${API_V1}/bob/${bobId}/update`,
  // Source & suggestion endpoints
  ADD_SOURCE: (bobId: string) => `${API_V1}/bob/${bobId}/add-source`,
  SUGGESTIONS: (bobId: string) => `${API_V1}/bob/${bobId}/suggestions`,
  MERGE_SUGGESTION: (bobId: string, suggestionId: string) =>
    `${API_V1}/bob/${bobId}/suggestions/${suggestionId}/merge`,
  REJECT_SUGGESTION: (bobId: string, suggestionId: string) =>
    `${API_V1}/bob/${bobId}/suggestions/${suggestionId}/reject`,
  AVAILABLE_SCANS: (bobId: string) => `${API_V1}/bob/${bobId}/available-scans`,
  SOURCE_HISTORY: (bobId: string) => `${API_V1}/bob/${bobId}/source-history`,
} as const

// Crisis endpoints
export const CRISIS_ENDPOINTS = {
  LIST: `${API_V1}/crisis/`,
  ACTIVE: `${API_V1}/crisis/active`,
  CRISIS_BY_ID: (id: string) => `${API_V1}/crisis/${id}`,
} as const

// System endpoints
export const SYSTEM_ENDPOINTS = {
  HEALTH: `${API_V1}/system/health`,
  INFO: `${API_V1}/system/info`,
  METRICS: `${API_V1}/system/metrics`,
  QUEUES: `${API_V1}/system/jobs/queues`,
  WORKERS: `${API_V1}/system/jobs/workers`,
  RECENT_JOBS: (limit: number = 50) => `${API_V1}/system/jobs/recent?limit=${limit}`,
} as const

// Audit log endpoints
export const AUDIT_LOG_ENDPOINTS = {
  LIST: `${API_V1}/logs/`,
  MY_ACTIVITY: `${API_V1}/logs/my-activity`,
  CRISIS_LOGS: (crisisId: string) => `${API_V1}/logs/crisis/${crisisId}`,
  RESOURCE_LOGS: (resourceType: string, resourceId: string) =>
    `${API_V1}/logs/resource/${resourceType}/${resourceId}`,
  ACTIONS: `${API_V1}/logs/actions`,
  LOG_BY_ID: (logId: string) => `${API_V1}/logs/${logId}`,
  EXPORT: `${API_V1}/logs/export`,
} as const

// Writing assistant endpoints
export const WRITING_ENDPOINTS = {
  NEW: `${API_V1}/writing/new`,
  LIST: `${API_V1}/writing/`,
  WRITING_BY_ID: (writingId: string) => `${API_V1}/writing/${writingId}`,
  DELETE: (writingId: string) => `${API_V1}/writing/${writingId}`,
  UPDATE_CONTENT: (writingId: string) => `${API_V1}/writing/${writingId}/update-content`,
  REGENERATE: (writingId: string) => `${API_V1}/writing/${writingId}/regenerate`,
  ADJUST_CONTENT: (writingId: string) => `${API_V1}/writing/${writingId}/adjust-content`,
  TRANSLATE: (writingId: string) => `${API_V1}/writing/${writingId}/translate`,
} as const

// Practice case endpoints
export const PRACTICE_CASE_ENDPOINTS = {
  LIST: `${API_V1}/practice-case/cases`,
  CREATE: `${API_V1}/practice-case/cases`,
  CASE_BY_ID: (caseId: string) => `${API_V1}/practice-case/cases/${caseId}`,
  ARTICLES: (caseId: string) => `${API_V1}/practice-case/cases/${caseId}/articles`,
  UPDATE: (caseId: string) => `${API_V1}/practice-case/cases/${caseId}`,
  DELETE: (caseId: string) => `${API_V1}/practice-case/cases/${caseId}`,
  PUBLIC_LIST: `${API_V1}/practice-case/practice-cases`,
} as const

// Contacts endpoints
export const CONTACTS_ENDPOINTS = {
  // LIST: `${API_V1}/contacts/list`,
  // FILES: `${API_V1}/contacts/files`,
  CONTACTGEGEVENS: `${API_V1}/contacts/contactgegevens`,
  CLEANUP: `${API_V1}/contacts/cleanup`,
  REFRESH: `${API_V1}/contacts/refresh`,
} as const

// Endpoint configuration
export const INTEGRATION_ENDPOINTS = {
  LIST: '/api/v1/integration',
  BY_TYPE: (type: string) => `/api/v1/integration/${type}`,
  SHAREPOINT: '/api/v1/integration/sharepoint',
  SHAREPOINT_UPLOAD_KEY: '/api/v1/integration/sharepoint/upload-key',
  COOSTO: '/api/v1/integration/coosto',
  TOGGLE: (type: string) => `/api/v1/integration/${type}/toggle`,
  TEST: (type: string) => `/api/v1/integration/${type}/test`,
}

export const DOCUMENTS_ENDPOINTS = {
  SEARCH: `${API_V1}/documents/search`,
  BROWSE: `${API_V1}/documents/browse`,
  BY_FOLDER: `${API_V1}/documents/by-folder`,
  INDEX: `${API_V1}/documents/index`,
  INDEX_STATUS: (crisisId: string) => `${API_V1}/documents/index-status/${crisisId}`,
  DOWNLOAD: (documentId: string) => `${API_V1}/documents/download/${documentId}`,
} as const

export const INDEXED_FOLDERS_ENDPOINTS = {
  LIST: `${API_V1}/indexed-folders/`,
  FOLDER_BY_ID: (id: string) => `${API_V1}/indexed-folders/${id}`,
  TOGGLE: (id: string) => `${API_V1}/indexed-folders/${id}/toggle`,
  FOLDER_STATUS: (folderId: string) => `${API_V1}/indexed-folders/${folderId}/status`,
  ALL_STATUSES: `${API_V1}/indexed-folders/statuses`,
  INDEX: `${API_V1}/indexed-folders/index`,
} as const
